﻿(function () {
  const SELECTOR = '[data-featured-tabs-section]';
  const BANNER_SELECTOR = '[data-vgtb-banner]';

  /** 计算页面头部当前可见区域高度，用于设置吸顶偏移。 */
  function getHeaderOffset() {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection) return 0;

    const header = headerSection.querySelector('.header[is="sticky-header"], .header[is="custom-header"], .header');
    if (!header) return 0;

    const stickyType = header.getAttribute('data-sticky-type') || headerSection.getAttribute('data-sticky-type') || '';
    if (stickyType === 'on-scroll-up' && headerSection.classList.contains('header-hidden')) {
      return 0;
    }

    // 使用稳定高度而非 transform 过渡中的 bottom，避免上滑时出现偏移计算瞬时过小。
    const stickyHeightVar = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height') || '0',
      10
    );

    if (!Number.isNaN(stickyHeightVar) && stickyHeightVar > 0) {
      return stickyHeightVar;
    }

    return Math.max(0, Math.round(header.offsetHeight || header.clientHeight || header.getBoundingClientRect().height || 0));
  }

  class FeaturedCollectionTabs {
    /** 初始化 Tab 实例，缓存节点并挂载交互逻辑。 */
    constructor(section) {
      this.section = section;
      this.sticky = section.querySelector('[data-tabs-sticky]');
      this.nav = section.querySelector('[data-tabs-nav]');
      this.toggle = section.querySelector('[data-tabs-toggle]');
      this.buttons = Array.from(section.querySelectorAll('[data-tab-target]'));
      this.panels = Array.from(section.querySelectorAll('[data-tab-content]'));
      this.panelBodies = Array.from(section.querySelectorAll('[data-tab-panel-body]'));
      this.expandLabel = section.querySelector('[data-label-expand]');
      this.collapseLabel = section.querySelector('[data-label-collapse]');
      this.collapsedLines = parseInt(section.dataset.collapsedLines || '2', 10);
      this.defaultExpanded = section.dataset.defaultExpanded === 'expanded';
      this.urlParamName = (section.dataset.urlParam || 'tab').trim();
      this.expanded = this.defaultExpanded;
      this.canCollapse = false;
      this.activeIndex = 1;
      this.raf = null;
      this.scrollLockUntil = 0;
      this.lastPanelRecalcAt = 0;
      this.panelResizeObserver = null;
      this.panelRecalcFrame = null;
      this.isRecalculatingPanels = false;
      this.collapseCompensationFrame = null;

      if (!this.sticky || !this.nav || this.buttons.length === 0 || this.panels.length === 0) return;

      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onHeaderStateChanged = this.onHeaderStateChanged.bind(this);
      this.onPanelToggle = this.onPanelToggle.bind(this);
      this.onHashChange = this.onHashChange.bind(this);

      this.bindEvents();
      this.updateStickyOffset();
      this.repositionMobilePromos();
      this.evaluateCollapse();
      this.applyExpandedState();
      this.setupPanelCollapses();
      this.initPanelCollapseObservers();
      window.addEventListener('load', () => { this.repositionMobilePromos(); this.setupPanelCollapses(); }, { once: true });
      this.activateTabByIndex(1);
      this.scheduleInitialHashSync();
    }

    /** 绑定组件所需的点击、滚动、尺寸及哈希事件。 */
    bindEvents() {
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.goToTab(button.dataset.tabAnchor || button.dataset.tabIndex, false, true));
      });
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('hashchange', this.onHashChange);
      document.addEventListener('header:scrolled', this.onHeaderStateChanged, { passive: true });
      this.section.addEventListener('click', this.onPanelToggle);
    }

    /** 销毁实例并移除监听，释放动画帧与观察器。 */
    destroy() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('hashchange', this.onHashChange);
      document.removeEventListener('header:scrolled', this.onHeaderStateChanged);
      this.section.removeEventListener('click', this.onPanelToggle);
      this.panelResizeObserver?.disconnect();
      if (this.panelRecalcFrame) cancelAnimationFrame(this.panelRecalcFrame);
      if (this.collapseCompensationFrame) cancelAnimationFrame(this.collapseCompensationFrame);
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    }

    /** 在窗口尺寸变化时重算吸顶与折叠状态。 */
    onResize() {
      this.updateStickyOffset();
      this.repositionMobilePromos();
      this.evaluateCollapse();
      this.applyExpandedState();
      this.setupPanelCollapses();
    }

    /** 在移动端将促销视频/图文卡片重新定位到其“移动端位置”，PC 端恢复为原位置。 */
    repositionMobilePromos() {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const promoSelector = '.product-card--promo, .product-card--promo-image, .product-card--promo-video';
      this.panels.forEach((panel) => {
        const body = panel.querySelector('[data-tab-panel-body]');
        const grid = body && body.querySelector('.card-grid');
        if (!grid) return;
        const tabIndex = parseInt(panel.dataset.tabIndex || '1', 10);
        grid.querySelectorAll(promoSelector).forEach((promo) => {
          if (promo.dataset.promoTabIndex && parseInt(promo.dataset.promoTabIndex, 10) !== tabIndex) return;
          const desktopPos = parseInt(promo.dataset.promoPosition || '0', 10);
          const mobilePos = parseInt(promo.dataset.promoPositionMobile || promo.dataset.promoPosition || '0', 10);
          const targetPos = isMobile ? mobilePos : desktopPos;
          if (!targetPos || targetPos < 1) return;
          const productCards = Array.from(grid.children).filter(
            (el) =>
              el.classList.contains('product-card') &&
              !el.classList.contains('product-card--promo') &&
              !el.classList.contains('product-card--promo-image') &&
              !el.classList.contains('product-card--promo-video')
          );
          const ref = productCards[targetPos - 1];
          if (ref && promo !== ref) {
            grid.insertBefore(promo, ref);
          } else if (!ref) {
            grid.appendChild(promo);
          }
        });
      });
    }

    /** 在滚动时节流处理吸顶偏移、面板高度与激活态同步。 */
    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateStickyOffset();

        // 平滑滚动到目标区域期间，保持当前点击的 tab 高亮不被滚动联动覆盖。
        if (Date.now() < this.scrollLockUntil) return;
        this.maybeRecalcPanelCollapses();
        this.syncActiveTabFromViewport();
      });
    }

    /** 头部状态变化时更新吸顶偏移。 */
    onHeaderStateChanged() {
      this.updateStickyOffset();
    }

    /** 切换导航区展开/收起状态。 */
    onToggle() {
      if (!this.canCollapse) return;
      this.expanded = !this.expanded;
      this.applyExpandedState();
    }

    /** 处理单个内容面板的展开/收起按钮点击。 */
    onPanelToggle(event) {
      const button = event.target.closest('[data-tab-panel-toggle]');
      if (!button) return;

      const panel = button.closest('[data-tab-content]');
      if (!panel) return;
      const willCollapse = panel.dataset.contentExpanded === 'true';
      const buttonTopBefore = button.getBoundingClientRect().top;
      // 仅在「收起」时做补偿；展开时不需要锁定视口位置。
      const shouldCompensateOnCollapse = willCollapse;

      // 切换前先重新计算当前面板高度，避免使用过期高度导致跳动。
      this.updateSinglePanelCollapse(panel);
      panel.dataset.contentExpanded = panel.dataset.contentExpanded === 'true' ? 'false' : 'true';
      this.applyPanelCollapse(panel);

      // 收起动画会改变内容高度，这里在动画期间持续补偿滚动，避免视觉上“内容被顶走”。
      if (shouldCompensateOnCollapse) {
        this.compensateCollapseScroll(button, buttonTopBefore, 320);
      }
    }

    /** 在内容收起动画期间补偿滚动，尽量锁定按钮视口位置。 */
    compensateCollapseScroll(button, lockedTop, duration = 320) {
      // 如果已有补偿任务，先取消，避免多次点击时叠加抖动。
      if (this.collapseCompensationFrame) {
        cancelAnimationFrame(this.collapseCompensationFrame);
        this.collapseCompensationFrame = null;
      }

      const startedAt = performance.now();
      const step = (now) => {
        const currentTop = button.getBoundingClientRect().top;
        const delta = currentTop - lockedTop;
        // 让按钮尽量停留在点击前的视口位置。
        if (Math.abs(delta) > 0.5) {
          window.scrollBy(0, delta);
        }

        if (now - startedAt < duration) {
          this.collapseCompensationFrame = requestAnimationFrame(step);
        } else {
          this.collapseCompensationFrame = null;
        }
      };

      this.collapseCompensationFrame = requestAnimationFrame(step);
    }

    /** 监听 URL hash 变化并尝试跳转到对应 Tab。 */
    onHashChange() {
      this.applyHashFromLocation();
    }

    /** 在初始化与页面加载后多次尝试同步 hash，提升命中稳定性。 */
    scheduleInitialHashSync() {
      this.applyHashFromLocation();
      window.addEventListener('load', () => this.applyHashFromLocation(), { once: true });
      setTimeout(() => this.applyHashFromLocation(), 500);
    }

    /** 从当前 URL hash 读取目标并执行 Tab 跳转。 */
    applyHashFromLocation() {
      const hash = this.getInitialHashTab();
      if (!hash) return;
      this.goToTab(hash, true);
    }

    /** 计算并写入吸顶偏移 CSS 变量。 */
    updateStickyOffset() {
      const extra = parseInt(this.section.dataset.stickyExtra || '0', 10) || 0;
      const offset = getHeaderOffset() + extra;
      this.section.style.setProperty('--tabs-sticky-offset', `${Math.max(offset, 0)}px`);
    }

    /** 获取滚动定位时需要扣除的总偏移高度。 */
    getScrollOffset() {
      return this.sticky.getBoundingClientRect().height + (parseInt(getComputedStyle(this.section).getPropertyValue('--tabs-sticky-offset') || '0', 10) || 0) + 10;
    }

    /** 按节流策略触发面板可折叠状态重算。 */
    maybeRecalcPanelCollapses(force = false) {
      if (this.isRecalculatingPanels) return;
      const now = Date.now();
      if (!force && now - this.lastPanelRecalcAt < 350) return;
      this.lastPanelRecalcAt = now;
      this.isRecalculatingPanels = true;
      try {
        this.setupPanelCollapses();
      } finally {
        this.isRecalculatingPanels = false;
      }
    }

    /** 初始化面板尺寸与图片加载观察，驱动高度重算。 */
    initPanelCollapseObservers() {
      if (window.ResizeObserver) {
        this.panelResizeObserver = new ResizeObserver(() => {
          if (this.panelRecalcFrame) return;
          this.panelRecalcFrame = requestAnimationFrame(() => {
            this.panelRecalcFrame = null;
            this.maybeRecalcPanelCollapses();
          });
        });
        this.panels.forEach((panel) => {
          const body = panel.querySelector('[data-tab-panel-body]');
          const grid = body?.querySelector('.card-grid');
          // 仅监听网格尺寸变化，避免 body 的 max-height 写入触发反馈循环。
          if (grid) this.panelResizeObserver.observe(grid);
        });
      }

      this.panels.forEach((panel) => {
        panel.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', () => this.maybeRecalcPanelCollapses(true), { once: true });
        });
      });
    }

    /** 批量更新所有面板的折叠参数。 */
    setupPanelCollapses() {
      this.panels.forEach((panel) => this.updateSinglePanelCollapse(panel));
    }

    /** 重算单个面板的折叠高度、可折叠性与按钮状态。 */
    updateSinglePanelCollapse(panel) {
      const body = panel.querySelector('[data-tab-panel-body]');
      const toggle = panel.querySelector('[data-tab-panel-toggle]');
      if (!body || !toggle) return;

      if (!panel.dataset.contentExpanded) {
        const defaultExpanded = panel.dataset.contentDefaultExpanded === 'expanded';
        panel.dataset.contentExpanded = defaultExpanded ? 'true' : 'false';
      }

      const grid = body.querySelector('.card-grid');
      if (!grid || grid.children.length < 2) {
        panel.dataset.contentCanCollapse = 'false';
        toggle.hidden = true;
        body.style.maxHeight = '';
        return;
      }

      const children = Array.from(grid.children).filter((item) => item.getBoundingClientRect().height > 0);
      if (children.length < 2) {
        panel.dataset.contentCanCollapse = 'false';
        toggle.hidden = true;
        body.style.maxHeight = '';
        return;
      }

      const nonPromotionChildren = children.filter(
        (item) => !item.matches('.product-card--promo, .product-card--promo-image, .product-card--promo-video')
      );
      // 计算“收起后只显示第一行”时，优先使用真实商品卡片。
      // 若当前 tab 没有商品卡片，再回退为使用全部卡片（含促销卡）计算。
      const rowMeasureChildren = nonPromotionChildren.length > 0 ? nonPromotionChildren : children;

      const bodyRect = body.getBoundingClientRect();
      const rows = [];

      rowMeasureChildren.forEach((item) => {
        const rect = item.getBoundingClientRect();
        let row = rows.find((entry) => Math.abs(entry.top - rect.top) <= 3);
        if (!row) {
          row = { top: rect.top, items: [] };
          rows.push(row);
        }
        row.items.push(rect);
      });

      rows.sort((a, b) => a.top - b.top);

      const fullHeight = Math.ceil(body.scrollHeight);
      const firstRowBottom = rows.length
        ? Math.max(...rows[0].items.map((rect) => rect.bottom))
        : bodyRect.top;
      // 仅保留第一行可见，避免露出第二行内容。
      const collapsedHeight = Math.max(0, Math.floor(firstRowBottom - bodyRect.top - 1));
      const canCollapse = rows.length > 1 && (fullHeight - collapsedHeight) > 16;

      panel.dataset.contentCanCollapse = canCollapse ? 'true' : 'false';
      panel.dataset.contentCollapsedHeight = String(collapsedHeight);
      panel.dataset.contentFullHeight = String(fullHeight);
      toggle.hidden = !canCollapse;

      if (!canCollapse) {
        body.style.maxHeight = '';
        return;
      }

      this.applyPanelCollapse(panel);
    }

    /** 将单个面板的折叠状态应用到样式与可访问属性。 */
    applyPanelCollapse(panel) {
      const body = panel.querySelector('[data-tab-panel-body]');
      const toggle = panel.querySelector('[data-tab-panel-toggle]');
      if (!body || !toggle) return;
      if (panel.dataset.contentCanCollapse !== 'true') return;

      const expanded = panel.dataset.contentExpanded === 'true';
      const collapsedHeight = parseInt(panel.dataset.contentCollapsedHeight || '0', 10);
      const fullHeight = parseInt(panel.dataset.contentFullHeight || '0', 10) || Math.ceil(body.scrollHeight);

      body.style.maxHeight = `${expanded ? fullHeight : collapsedHeight}px`;

      const expandLabel = toggle.querySelector('[data-content-label-expand]');
      const collapseLabel = toggle.querySelector('[data-content-label-collapse]');
      if (expandLabel) expandLabel.hidden = expanded;
      if (collapseLabel) collapseLabel.hidden = !expanded;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    /** 根据视口位置同步当前应高亮的 Tab。 */
    syncActiveTabFromViewport() {
      const anchorY = this.getScrollOffset() + 16;
      let nearestIndex = this.activeIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;
      let nearestPanel = null;

      this.panels.forEach((panel) => {
        const panelIndex = parseInt(panel.dataset.tabIndex || '1', 10);
        const distance = Math.abs(panel.getBoundingClientRect().top - anchorY);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = panelIndex;
          nearestPanel = panel;
        }
      });

      if (nearestPanel) {
        const activeButton = this.buttons.find((button) => button.getAttribute('aria-selected') === 'true');
        if (activeButton && activeButton.dataset.tabTarget === nearestPanel.id) {
          return;
        }

        const sameTargetButton = this.buttons.find((button) => button.dataset.tabTarget === nearestPanel.id);
        if (sameTargetButton) {
          const sameTargetIndex = parseInt(sameTargetButton.dataset.tabIndex || '0', 10);
          if (!Number.isNaN(sameTargetIndex) && sameTargetIndex > 0) {
            this.activateTabByIndex(sameTargetIndex);
            return;
          }
        }
      }

      this.activateTabByIndex(nearestIndex);
    }

    /** 评估导航是否需要折叠，并更新开关显示。 */
    evaluateCollapse() {
      const rowTops = [...new Set(this.buttons.map((button) => button.offsetTop))];
      this.canCollapse = rowTops.length > this.collapsedLines;
      if (!this.toggle) return;
      this.toggle.hidden = !this.canCollapse;
      if (!this.canCollapse) this.expanded = true;
      if (!this.canCollapse) this.nav.style.maxHeight = '';
    }

    /** 应用导航展开/收起状态及对应标签文案。 */
    applyExpandedState() {
      if (!this.canCollapse) {
        this.nav.style.maxHeight = '';
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
        if (this.expandLabel) this.expandLabel.hidden = false;
        if (this.collapseLabel) this.collapseLabel.hidden = true;
        return;
      }

      if (this.expanded) {
        this.nav.style.maxHeight = `${this.nav.scrollHeight}px`;
      } else {
        const rowTops = [...new Set(this.buttons.map((button) => button.offsetTop))];
        const allowedTop = rowTops[this.collapsedLines - 1];
        let collapsedHeight = 0;
        this.buttons.forEach((button) => {
          if (button.offsetTop <= allowedTop) {
            collapsedHeight = Math.max(collapsedHeight, button.offsetTop + button.offsetHeight);
          }
        });
        this.nav.style.maxHeight = `${collapsedHeight}px`;
      }

      if (this.expandLabel) this.expandLabel.hidden = this.expanded;
      if (this.collapseLabel) this.collapseLabel.hidden = !this.expanded;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
    }

    /** 按索引激活对应 Tab 按钮。 */
    activateTabByIndex(index) {
      if (!index || Number.isNaN(index)) return;
      this.activeIndex = index;
      this.buttons.forEach((button) => {
        const currentIndex = parseInt(button.dataset.tabIndex || '0', 10);
        button.setAttribute('aria-selected', currentIndex === index ? 'true' : 'false');
      });
    }

    /** 归一化 Tab 锚点文本，便于匹配。 */
    normalizeTabAnchor(value) {
      const raw = String(value || '').trim().toLowerCase();
      if (!raw) return '';

      let decoded = raw;
      try {
        decoded = decodeURIComponent(raw);
      } catch (error) {
        decoded = raw;
      }

      return decoded
        .replace(/\+/g, ' ')
        .replace(/[\s_-]+/g, '-')
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    /** 生成锚点匹配用的多种键值集合。 */
    getAnchorKeys(value) {
      const normalized = this.normalizeTabAnchor(value);
      const compact = normalized.replace(/-/g, '');
      const plain = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\+/g, ' ')
        .replace(/[^a-z0-9]+/g, '');

      return new Set([normalized, compact, plain].filter(Boolean));
    }

    /** 根据索引或锚点文本查找目标 Tab 按钮。 */
    getTabButton(value) {
      const byIndex = Number.parseInt(value, 10);
      if (!Number.isNaN(byIndex)) {
        return this.buttons.find((button) => parseInt(button.dataset.tabIndex || '0', 10) === byIndex);
      }
      const targetKeys = this.getAnchorKeys(value);

      return this.buttons.find((button) => {
        const candidates = [
          this.normalizeTabAnchor(button.dataset.tabAnchor || ''),
          this.normalizeTabAnchor(button.dataset.tabAnchorRaw || ''),
          this.normalizeTabAnchor(button.textContent || '')
        ].filter(Boolean);

        return candidates.some((candidate) => {
          const candidateKeys = this.getAnchorKeys(candidate);
          for (const key of candidateKeys) {
            if (targetKeys.has(key)) return true;
          }
          return false;
        });
      });
    }

    /** 更新地址栏 hash，不触发历史堆栈新增。 */
    updateUrlHash(value) {
      const normalized = this.normalizeTabAnchor(value);
      if (!normalized) return;
      const url = new URL(window.location.href);
      url.hash = normalized;
      window.history.replaceState({}, '', url.toString());
    }

    /** 将指定面板强制切换为展开状态。 */
    expandPanelContent(panel) {
      if (!panel) return;
      this.updateSinglePanelCollapse(panel);
      if (panel.dataset.contentCanCollapse !== 'true') return;
      panel.dataset.contentExpanded = 'true';
      this.applyPanelCollapse(panel);
    }

    /** 导航到指定 Tab，并在需要时同步 URL 与面板展开状态。 */
    goToTab(value, fromUrlParam = false, expandPanelOnNavigate = false) {
      const button = this.getTabButton(value);
      if (!button) return;

      const target = this.section.querySelector(`#${button.dataset.tabTarget}`);
      if (!target) return;

      const tabIndex = parseInt(button.dataset.tabIndex || '1', 10);
      this.activateTabByIndex(tabIndex);
      this.scrollLockUntil = Date.now() + (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650);
      if (fromUrlParam) this.expanded = true;
      this.applyExpandedState();

      const scrollToTarget = (behavior = 'smooth') => {
        const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
        window.scrollTo({
          top: Math.max(0, top),
          behavior
        });
      };

      if (fromUrlParam) {
        scrollToTarget('auto');
        setTimeout(() => scrollToTarget('auto'), 160);
        setTimeout(() => scrollToTarget('auto'), 520);
      } else {
        scrollToTarget(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
      }

      if (!fromUrlParam) {
        this.updateUrlHash(button.dataset.tabAnchorRaw || button.dataset.tabAnchor || tabIndex);
      }
      button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

      if (expandPanelOnNavigate || fromUrlParam) {
        this.expandPanelContent(target);
      }

      if (fromUrlParam) {
        // 哈希导航可能发生在图片/网格尚未稳定时。
        // 稍后再重算并重新应用展开状态，确保高度与按钮状态正确。
        setTimeout(() => {
          this.maybeRecalcPanelCollapses(true);
          this.expandPanelContent(target);
        }, 260);
      }

      setTimeout(() => {
        this.maybeRecalcPanelCollapses(true);
        this.syncActiveTabFromViewport();
      }, 700);
    }

    /** 读取并解析当前 URL hash 作为初始 Tab 值。 */
    getInitialHashTab() {
      const hash = window.location.hash || '';
      if (!hash || hash === '#') return null;
      const value = hash.slice(1).trim();
      if (!value) return null;
      try {
        return decodeURIComponent(value);
      } catch (error) {
        return value;
      }
    }
  }

  function normalizeMountId(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getOrCreateOriginPlaceholder(element) {
    if (element.__vgtbOriginPlaceholder) return element.__vgtbOriginPlaceholder;
    const placeholder = document.createElement('span');
    placeholder.hidden = true;
    placeholder.setAttribute('data-vgtb-origin-placeholder', '');
    element.parentNode?.insertBefore(placeholder, element);
    element.__vgtbOriginPlaceholder = placeholder;
    return placeholder;
  }

  function restoreBannerToOrigin(element) {
    const placeholder = element.__vgtbOriginPlaceholder;
    if (!placeholder || !placeholder.parentNode) return;
    if (placeholder.nextSibling === element) return;
    placeholder.parentNode.insertBefore(element, placeholder.nextSibling);
  }

  function mountExternalVideoGradientBanners() {
    const slotMap = new Map();
    document.querySelectorAll(`${SELECTOR} [data-tab-banner-slot]`).forEach((slot) => {
      const mountId = normalizeMountId(slot.dataset.tabMountId);
      if (!mountId || slotMap.has(mountId)) return;
      slotMap.set(mountId, slot);
    });

    document.querySelectorAll(BANNER_SELECTOR).forEach((banner) => {
      const enabled = banner.dataset.vgtbMountEnabled === 'true';
      const mountId = normalizeMountId(banner.dataset.vgtbMountId);

      getOrCreateOriginPlaceholder(banner);

      if (!enabled || !mountId) {
        restoreBannerToOrigin(banner);
        banner.removeAttribute('data-vgtb-mounted');
        return;
      }

      const targetSlot = slotMap.get(mountId);
      if (!targetSlot) {
        // 未匹配到 tab：保持在原本配置位置显示
        restoreBannerToOrigin(banner);
        banner.removeAttribute('data-vgtb-mounted');
        return;
      }

      if (banner.parentNode !== targetSlot) {
        targetSlot.prepend(banner);
      }
      banner.setAttribute('data-vgtb-mounted', 'true');
    });
  }

  /** 初始化页面内所有 Featured Tabs 区块。 */
  function init() {
    document.querySelectorAll(SELECTOR).forEach((section) => {
      if (section.__featuredTabs) return;
      section.__featuredTabs = new FeaturedCollectionTabs(section);
    });
    mountExternalVideoGradientBanners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
